import { first } from 'lodash';
import { useInfiniteQuery } from 'react-query';
import { queryGet } from '@pocapan/shared/utils/queryClient';

const DEFAULT_PAGE = 15;

// 공통적인 useChatRoomsQuery 함수
const CATEGORY_MAP = {
  Purchasing: ['purchasing'],
  Selling: ['selling'],
  Exchanging: ['exchanging']
};

const BASE_KEY_MAP = {
  All: 'AllChatRooms',
  Purchasing: 'PurchasingChatRooms',
  Selling: 'SellingChatRooms',
  Exchanging: 'ExchangingChatRooms',
  DM: 'DMChatRooms'
};

export default function useChatRoomsQuery(
  {
    type = 'All',
    tradingPhotoCardId,
    onlyJoined = false,
    onlyMine = false,
    onlyBettie = false,
    perPage = DEFAULT_PAGE,
    categories: categoriesOverride,
    categoryFilterType
  },
  queryOptions = {}
) {
  const filters = {
    only_joined: onlyJoined,
    only_mine: onlyMine,
    only_dm: type === 'DM',
    only_bettie: onlyBettie,
    trading_photo_card_id: tradingPhotoCardId,
    categories: categoriesOverride ?? CATEGORY_MAP[type],
    category_filter_type: categoryFilterType
  };

  return useChatRoomsQueryCore({
    baseKey: BASE_KEY_MAP[type] || BASE_KEY_MAP.All,
    baseUrl: '/api/v1/chat_rooms',
    filters,
    perPage,
    enabled: tradingPhotoCardId !== undefined ? !!tradingPhotoCardId : true,
    queryOptions
  });
}

// 공통 로직을 처리하는 core 함수
function useChatRoomsQueryCore({
  baseKey,
  baseUrl,
  filters = {},
  perPage,
  enabled,
  queryOptions = {}
}) {
  const queryParams = createFilteredQueryParams({ perPage, filters });
  const cacheKey = generateCacheKey(baseKey, filters);

  const { data, isFetching, fetchNextPage, hasNextPage } = useInfiniteQuery(
    cacheKey,
    ({ pageParam = 1 }) => fetchChatRoomsQuery({ pageParam, url: baseUrl, queryParams }),
    {
      enabled,
      getNextPageParam: (lastPage) => lastPage?.meta?.pagination?.nextPage || undefined,
      // 기본값
      staleTime: 1000 * 5,
      cacheTime: 1000 * 10,
      // 페이지별 오버라이드 허용
      ...queryOptions
    }
  );

  return { data, isFetching, fetchNextPage, hasNextPage };
}

function createFilteredQueryParams({ perPage = DEFAULT_PAGE, page = 1, filters = {} }) {
  const queryParams = new URLSearchParams({ per_page: perPage, page });

  Object.keys(filters).forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== false) {
      if (Array.isArray(value)) {
        for (const v of value) queryParams.append(`${key}[]`, v);
      } else {
        queryParams.append(key, value);
      }
    }
  });

  return queryParams;
}

function generateCacheKey(baseKey, filters) {
  const filterKey = Object.entries(filters)
    .filter(([_, value]) => value !== undefined && value !== false)
    .map(([key, value]) => (Array.isArray(value) ? `${key}:${value.join(',')}` : `${key}:${value}`))
    .join('&');

  return [baseKey, filterKey];
}

// ChatRooms 데이터를 가져오는 함수 (기존)
async function fetchChatRoomsQuery({ pageParam = 1, url, queryParams }) {
  queryParams.set('page', pageParam);
  const finalUrl = `${url}?${queryParams.toString()}`;

  try {
    const data = await queryGet(finalUrl);
    return data;
  } catch (_error) {
    throw new Error('Error fetching paginated data.');
  }
}

export async function fetchRecentChatRoomQuery({ tradingPhotoCardId }) {
  const filters = { trading_photo_card_id: tradingPhotoCardId };
  const queryParams = createFilteredQueryParams({ perPage: 1, filters });
  const url = '/api/v1/chat_rooms';

  try {
    const data = await queryGet(`${url}?${queryParams.toString()}`);
    return first(data.chatRooms);
  } catch (_error) {
    throw new Error('Error fetching recent chat room.');
  }
}
